import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import ReactCalendar from 'react-calendar';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { VscLoading } from 'react-icons/vsc';
import { DateUtils } from '../../helpers/dateUtils';
import { addMonthsToDate } from '../../helpers/utils';
import { useCalendarPrices } from '../../hooks/useCalendarPrices';
import calendarUtils from './calendarUtils';
import { ReactCalendarWrapper } from './styles';

interface Props {
    value: Date;
    setValue: (d: Date) => void;
    pricesProductId?: string;
    ignoreBasicValidation?: boolean;
    forceViewDate?: number
    isHeadless?: boolean
    forcedPrice?: number
    minDate?: Date
    maxDate?: Date
}

const currentMonth = new Date().getMonth();

export const CustomReactCalendar: FC<Props> = ({
    value,
    setValue,
    pricesProductId,
    ignoreBasicValidation,
    forceViewDate,
    isHeadless,
    forcedPrice,
    minDate,
    maxDate,
}) => {
    const [viewDate, setViewDate] = useState<Date>(value);
    const [rangeStart, rangeEnd] = DateUtils.getDateMonthRange(viewDate);
    const prices = useCalendarPrices(
        pricesProductId
            ? { productId: pricesProductId, rangeStart, rangeEnd }
            : {}
    );

    const changeValueMonth = (months: number) => () => {
        const newValue = addMonthsToDate(viewDate, months);
        setViewDate(newValue);
    };

    const isCurrentMonth = currentMonth === viewDate.getMonth();

    const isEntireMonthEmpty = useMemo(() => {
        if (prices.isPending) return false;
        if (!prices.data || Object.values(prices.data).length === 0) return true;
        const total = Object.values(prices.data).reduce((dayTotal, dayItem) => {
            const subDayTotal = dayItem.schedules.reduce((p, c) => c.available + p, 0);
            return dayTotal + subDayTotal
        }, 0);
        return total === 0;

    }, [viewDate.getMonth(), prices.data]);

    const checkIsValidDate = (targetDate: Date) => {
        if (!pricesProductId) return false;
        if (!prices.data) return false;
        const key = moment(targetDate).format('YYYY-MM-DD');
        const data = prices.data[key];

        if (!data) return false;
        if (data.schedules.length === 0) return false;
        const hasNoSchedules =
            data.schedules.reduce((p, c) => c.available + p, 0) === 0;

        if (hasNoSchedules) return false;

        return true;
    };

    useEffect(() => {
        if (checkIsValidDate(viewDate) || ignoreBasicValidation) {
            setValue(viewDate);
        }
    }, [viewDate]);

    useEffect(() => {
        if(forceViewDate) {
            setViewDate(value)
        }
    }, [forceViewDate, value])

    return (
        <ReactCalendarWrapper className={isHeadless ? "headless" : ""}>
            <ReactCalendar
                value={value}
                onChange={v => setViewDate(v as Date)}
                locale="pt"
                minDetail="month"
                selectRange={false}
                allowPartialRange={false}
                minDate={minDate || new Date()}
                maxDate={maxDate}
                formatShortWeekday={calendarUtils.formatShortWeekday}
                tileContent={calendarUtils.getTitleContent(
                    prices.data,
                    ignoreBasicValidation,
                    forcedPrice
                )}
                nextLabel={!isEntireMonthEmpty && <LuChevronRight onClick={changeValueMonth(1)} />}
                prevLabel={
                    !isCurrentMonth && (
                        <LuChevronLeft onClick={changeValueMonth(-1)} />
                    )
                }
                formatMonthYear={calendarUtils.formatMonthYear}
                defaultView="month"
                calendarType="hebrew"
                tileClassName={
                    ignoreBasicValidation
                        ? ''
                        : calendarUtils.getTileClassName(prices.data)
                }
                tileDisabled={({ date }) =>
                    ((!checkIsValidDate(date) && !ignoreBasicValidation) ||
                        (minDate ? date < minDate : false) ||
                        (maxDate ? date > maxDate : false))
                }
            ></ReactCalendar>
            <AnimatePresence>
                {pricesProductId && prices.isPending && (
                    <motion.div
                        className="loading-wrapper"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="spinner"
                            animate={{ rotate: [0, 360] }}
                            transition={{
                                repeat: Infinity,
                                ease: 'linear',
                                duration: 0.5,
                            }}
                        >
                            <VscLoading />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </ReactCalendarWrapper>
    );
};
